import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { expensesApi } from '../../../api/expenses.api';
import { isAlreadyGone } from '../../../api/client';
import ExpenseForm from '../../expenses/ExpenseForm';
import Modal from '../../ui/Modal';
import { toast } from '../../ui/Toast';
import { Icon, Plus, Wallet } from '../../ui/Icon';
import { expenseReservation, sameReservation, type ReservationGroups, type ReservationOption } from '../../../utils/reservations';
import type { Expense, ReservationLink, Trip } from '../../../types';
import { hidesTab } from '../../../utils/tripPermissions';

interface TicketExpensesValue {
  expensesFor: (link: ReservationLink) => Expense[];
  currency: string;
  open: (expense: Expense) => void;
  add: (reservation: ReservationOption) => void;
}

// Null outside the bookings tab: a ticket rendered anywhere else (a shared or
// read-only view) shows no money at all rather than an empty chip.
const TicketExpensesContext = createContext<TicketExpensesValue | null>(null);

type FormState = { kind: 'add'; reservation: ReservationOption } | { kind: 'edit'; expense: Expense };

const money = (n: number, currency: string) => `${n.toFixed(2)} ${currency}`;

/**
 * Loads the trip's expenses once for the bookings tab and owns the expense form
 * the tickets open, so each ticket only has to say which reservation it is.
 */
export function TicketExpensesScope({ trip, reservations, children }: {
  trip: Trip;
  reservations: ReservationGroups;
  children: ReactNode;
}) {
  const { t } = useTranslation();
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [form, setForm] = useState<FormState | null>(null);

  // ADR-0038: expenses are the travellers'; for agency staff the tickets carry none.
  const hidden = hidesTab(trip, 'expenses');
  const load = () => expensesApi.list(trip.id).then(setExpenses).catch(() => setExpenses([]));
  useEffect(() => { if (!hidden) void load(); }, [trip.id, hidden]); // eslint-disable-line react-hooks/exhaustive-deps

  // Debts are rebuilt on request, not on write — the expenses tab does the
  // same after every change, or balances would lag behind this expense.
  async function afterChange() {
    setForm(null);
    await expensesApi.recalculateDebts(trip.id).catch(() => undefined);
    await load();
  }

  async function remove(id: string) {
    try {
      await expensesApi.delete(trip.id, id);
    } catch (e) {
      if (!isAlreadyGone(e)) { toast(t('common.error'), 'error'); return; }
    }
    toast(t('expenses.toast.deleted'), 'success');
    await afterChange();
  }

  const value = useMemo<TicketExpensesValue>(() => ({
    expensesFor: link => expenses.filter(e => sameReservation(expenseReservation(e), link)),
    currency: trip.budgetCurrency || 'EUR',
    open: expense => setForm({ kind: 'edit', expense }),
    add: reservation => setForm({ kind: 'add', reservation }),
  }), [expenses, trip.budgetCurrency]);

  if (hidden) return <>{children}</>;
  return (
    <TicketExpensesContext.Provider value={value}>
      {children}
      <Modal
        open={!!form}
        onClose={() => setForm(null)}
        title={form?.kind === 'edit' ? t('expenses.editExpense') : t('expenses.addExpense')}
        size="lg"
      >
        {form && (
          <ExpenseForm
            key={form.kind === 'edit' ? form.expense.id : form.reservation.link.id}
            tripId={trip.id}
            trip={trip}
            existing={form.kind === 'edit' ? form.expense : undefined}
            initialReservation={form.kind === 'add' ? form.reservation : undefined}
            reservations={reservations}
            onSaved={afterChange}
            onCancel={() => setForm(null)}
            onDelete={form.kind === 'edit' ? () => remove(form.expense.id) : undefined}
          />
        )}
      </Modal>
    </TicketExpensesContext.Provider>
  );
}

function useLinkedExpenses(link: ReservationLink) {
  const ctx = useContext(TicketExpensesContext);
  if (!ctx) return null;
  const linked = ctx.expensesFor(link);
  // Stored amounts are already in the trip's base currency, so they add up.
  const total = linked.reduce((s, e) => s + Number(e.amount), 0);
  return { ...ctx, linked, total };
}

/** What's been paid for this ticket, for the collapsed header. Nothing if nothing. */
export function TicketExpenseBadge({ link, size = 10 }: { link: ReservationLink; size?: number }) {
  const { t } = useTranslation();
  const data = useLinkedExpenses(link);
  if (!data || data.linked.length === 0) return null;
  return (
    <span
      className="flex items-center gap-1 font-mono text-[11px] text-emerald-600 dark:text-emerald-400 font-semibold tabular-nums"
      title={t('bookings.expenses.paid')}
    >
      <Icon as={Wallet} size={size} /> {money(data.total, data.currency)}
    </span>
  );
}

/** Left side of the ticket's action bar: its expenses, each opens, and "add". */
export function TicketExpenseActions({ reservation }: { reservation: ReservationOption }) {
  const { t } = useTranslation();
  const data = useLinkedExpenses(reservation.link);
  if (!data) return null;
  return (
    <div className="flex items-center gap-1 flex-wrap mr-auto min-w-0">
      {data.linked.map(e => (
        <button
          key={e.id}
          type="button"
          onClick={ev => { ev.stopPropagation(); data.open(e); }}
          title={t('expenses.table.editRow', { description: e.description })}
          className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 text-[11px] font-semibold text-emerald-700 dark:text-emerald-300 hover:bg-emerald-100 dark:hover:bg-emerald-900/40 transition-colors cursor-pointer max-w-[220px]"
        >
          <Icon as={Wallet} size={11} className="shrink-0" />
          <span className="truncate">{e.description}</span>
          <span className="font-mono tabular-nums shrink-0">{money(Number(e.amount), data.currency)}</span>
        </button>
      ))}
      <button
        type="button"
        onClick={ev => { ev.stopPropagation(); data.add(reservation); }}
        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-700 dark:hover:text-slate-200 transition-colors cursor-pointer"
      >
        <Icon as={Plus} size={12} /> {t('bookings.expenses.add')}
      </button>
    </div>
  );
}
